<template>
  <dashboard-layout title="Adapty Webhooks">
    <template #actions>
      <search-box v-model="search" placeholder="Search..." @update:model-value="applyFilter" />
      <div class="build-filter">
        <input v-model="buildNumberFilter" placeholder="Filter by build number..." @input="applyFilter"/>
        <svg v-if="buildNumberFilter" @click="clearBuildFilter" class="clear-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </div>
      <button @click="openExportModal" class="btn-primary">Export CSV</button>
      <button @click="exportLifecycles" class="btn-primary lifecycle-btn" :disabled="exportingLifecycles">
        {{ exportingLifecycles ? 'Exporting...' : 'Export Lifecycles' }}
      </button>
    </template>

    <stat-grid>
      <stat-card label="Total events" :value="list.length.toLocaleString('en-US')" icon="activity" accent />
      <stat-card label="Event types" :value="distinctEventTypes" icon="filetext" />
      <stat-card label="Distinct users" :value="distinctUsers.toLocaleString('en-US')" icon="users" />
    </stat-grid>

    <data-panel>
      <div class="chart-header">
        <div class="chart-title-row">
          <div>
            <h2>Subscription Cancellation Rate</h2>
            <p class="chart-description">Shows the percentage of subscriptions that were cancelled after starting</p>
            <div class="chart-controls">
              <div class="group-by-selector">
                <button
                  @click="changeGroupBy('build')"
                  :class="['group-btn', { active: groupBy === 'build' }]"
                >
                  By Build
                </button>
                <button
                  @click="changeGroupBy('duration')"
                  :class="['group-btn', { active: groupBy === 'duration' }]"
                >
                  By Duration
                </button>
              </div>
              <div class="trailing-average-toggle">
                <label class="toggle-label">
                  <input
                    type="checkbox"
                    v-model="useTrailingAverage"
                    class="toggle-checkbox"
                  />
                  <span class="toggle-text">Use trailing average</span>
                </label>
                <div class="info-tooltip">
                  <svg class="info-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <span class="tooltip-text">Each data point is a trailing average of the last 7 days. Trailing averages help visualize long-term trends by smoothing out short-term fluctuations.</span>
                </div>
              </div>
              <div v-if="!chartLoading && chartData.length > 0" class="average-stat">
                <span class="stat-label">Average:</span>
                <span class="stat-value">{{ averageCancellationRate }}%</span>
                <span class="stat-detail">({{ totalCancelled }}/{{ totalStarted }})</span>
              </div>
            </div>
          </div>
          <div class="date-range-selector">
            <button
              v-for="range in dateRanges"
              :key="range.days"
              @click="selectDateRange(range.days)"
              :class="['range-btn', { active: selectedDays === range.days && !isCustomRange }]"
            >
              {{ range.label }}
            </button>
            <button
              @click="toggleCustomDatePicker"
              :class="['range-btn', { active: isCustomRange }]"
            >
              Custom
            </button>
          </div>
          <div v-if="showCustomDatePicker" class="custom-date-picker">
            <div class="date-input-group">
              <label>Start Date:</label>
              <input
                type="date"
                v-model="customStartDate"
                :max="customEndDate"
                class="date-input"
              />
            </div>
            <div class="date-input-group">
              <label>End Date:</label>
              <input
                type="date"
                v-model="customEndDate"
                :min="customStartDate"
                class="date-input"
              />
            </div>
            <button @click="applyCustomDateRange" class="apply-btn">Apply</button>
          </div>
        </div>
      </div>
      <loading-state v-if="chartLoading" label="Loading chart data..." />
      <empty-state v-else-if="chartData.length === 0" title="No chart data available" />
      <div v-else>
        <LineChart
          :data="displayChartData"
          :buildNumbers="buildNumbers"
          title="Cancellation Rate"
          color="#ff4757"
          @legend-click="handleLegendClick"
        />
      </div>
    </data-panel>

    <data-panel flush>
      <template #title>Webhook Events <span class="muted">({{ filtered.length.toLocaleString('en-US') }})</span></template>

      <loading-state v-if="loading" label="Loading..." />
      <data-table
        v-else
        :columns="columns"
        :rows="filtered"
        row-key="id"
      >
        <template #cell-event_type="{ row }">
          <status-badge v-if="row.event_type" :variant="eventVariant(row.event_type)" :label="row.event_type" />
          <span v-else class="muted">-</span>
        </template>
        <template #cell-event_datetime="{ row }">
          <span class="nowrap">{{ formatDate(row.event_datetime) }}</span>
        </template>
        <template #cell-customer_user_id="{ row }">{{ row.customer_user_id || '-' }}</template>
        <template #cell-profile_id="{ row }">{{ row.profile_id || '-' }}</template>
        <template #cell-receivedAt="{ row }">
          <span class="nowrap">{{ formatDate(row.receivedAt) }}</span>
        </template>
        <template #cell-idfv="{ row }">{{ row.idfv || '-' }}</template>
        <template #cell-user_agent="{ row }">{{ truncate(row.user_agent, 50) }}</template>
        <template #empty>No data found</template>
      </data-table>
    </data-panel>

    <div v-if="isExportModalVisible" class="modal-overlay" @click.self="closeExportModal">
      <div class="modal-content">
        <h3>Export Options</h3>
        <div class="filter-group">
          <p>Select an event type to export, or export all.</p>
          <select v-model="selectedEventType" class="event-type-select">
            <option v-for="eventType in uniqueEventTypes" :key="eventType" :value="eventType">
              {{ eventType }}
            </option>
          </select>
        </div>
        <div class="filter-group">
          <p>Select fields to export.</p>
          <div class="fields-container">
            <div v-for="field in allFields" :key="field" class="field-checkbox">
              <input type="checkbox" :id="`field-${field}`" :value="field" v-model="selectedFields">
              <label :for="`field-${field}`">{{ field }}</label>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button @click="closeExportModal" class="btn-secondary">Cancel</button>
          <button @click="handleExport" class="btn-primary">Export</button>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { FirebaseAdaptyService } from '../services/firebaseAdaptyService'
import { FirebaseSubscriptionLifecycleService } from '../services/firebaseSubscriptionLifecycleService'
import LineChart from './LineChart.vue'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const columns = [
  { key: 'event_type', label: 'Event Type', width: '180px' },
  { key: 'event_datetime', label: 'Event Datetime', width: '160px' },
  { key: 'customer_user_id', label: 'Customer User ID' },
  { key: 'profile_id', label: 'Profile ID' },
  { key: 'receivedAt', label: 'Received At', width: '160px' },
  { key: 'idfv', label: 'IDFV' },
  { key: 'user_agent', label: 'User Agent' }
]

export default {
  name: 'AdaptyWebhookDashboard',
  components: {
    LineChart, DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, LoadingState, EmptyState, StatusBadge
  },
  setup() {
    const service = new FirebaseAdaptyService()
    const lifecycleService = new FirebaseSubscriptionLifecycleService()
    const exportingLifecycles = ref(false)
    const list = ref([])
    const filtered = ref([])
    const search = ref('')
    const buildNumberFilter = ref('')
    const loading = ref(true)
    const isExportModalVisible = ref(false)
    const selectedEventType = ref('All')

    const chartData = ref([])
    const chartDetailData = ref([])
    const chartLoading = ref(false)
    const buildNumbers = ref([])
    const selectedDays = ref(7)
    const isCustomRange = ref(false)
    const showCustomDatePicker = ref(false)
    const averageCancellationRate = ref(0)
    const totalStarted = ref(0)
    const totalCancelled = ref(0)
    const groupBy = ref('build') // 'build' or 'duration'
    const useTrailingAverage = ref(false)

    const today = new Date().toISOString().split('T')[0]
    const defaultEndDate = new Date()
    const defaultStartDate = new Date()
    defaultStartDate.setDate(defaultStartDate.getDate() - 7)

    const customStartDate = ref(defaultStartDate.toISOString().split('T')[0])
    const customEndDate = ref(defaultEndDate.toISOString().split('T')[0])

    const dateRanges = [
      { label: '7 Days', days: 7 },
      { label: '14 Days', days: 14 },
      { label: '30 Days', days: 30 },
      { label: '60 Days', days: 60 },
      { label: '90 Days', days: 90 }
    ]

    const allFields = ref([
      "advertising_id", "customer_user_id", "email", "event_api_version", "event_datetime",
      "event_type", "event_properties", "vendor_product_id", "environment",
      "idfa", "idfv", "integration_ids", "profile_id",
      "profile_install_datetime", "receivedAt", "user_agent", "user_attributes", "build_number"
    ]);
    const selectedFields = ref([...allFields.value]);

    const uniqueEventTypes = computed(() => {
      if (!list.value) return ['All'];
      const eventTypes = list.value.map(item => item.event_type).filter(Boolean);
      return ['All', ...new Set(eventTypes)];
    });

    const distinctEventTypes = computed(() => Math.max(0, uniqueEventTypes.value.length - 1))

    const distinctUsers = computed(() => new Set(list.value.map(i => i.customer_user_id).filter(Boolean)).size)

    const eventVariant = (type) => {
      const t = String(type || '').toLowerCase()
      if (t.includes('cancel') || t.includes('refund') || t.includes('expired') || t.includes('billing_issue') || t.includes('churn')) return 'danger'
      if (t.includes('started') || t.includes('renewed') || t.includes('activated') || t.includes('purchase') || t.includes('converted')) return 'success'
      if (t.includes('trial')) return 'info'
      return 'primary'
    }

    const displayChartData = computed(() => {
      if (!useTrailingAverage.value || chartData.value.length === 0) {
        return chartData.value
      }

      const TRAILING_DAYS = 7

      // Helper function to calculate trailing average for a sub-group
      const calculateTrailingForSubGroup = (windowData, subGroupKey) => {
        // Collect ALL keys from ALL days in the window
        const allKeys = new Set()
        windowData.forEach(d => {
          if (d[subGroupKey]) {
            Object.keys(d[subGroupKey]).forEach(key => allKeys.add(key))
          }
        })

        if (allKeys.size === 0) return null

        const trailingSubGroups = {}

        allKeys.forEach(key => {
          let subGroupStarted = 0
          let subGroupCancelled = 0

          windowData.forEach(d => {
            if (d[subGroupKey] && d[subGroupKey][key]) {
              subGroupStarted += d[subGroupKey][key].started
              subGroupCancelled += d[subGroupKey][key].cancelled
            }
          })

          const subGroupValue = subGroupStarted > 0
            ? parseFloat(((subGroupCancelled / subGroupStarted) * 100).toFixed(1))
            : 0

          trailingSubGroups[key] = {
            started: subGroupStarted,
            cancelled: subGroupCancelled,
            rate: subGroupValue
          }
        })

        return trailingSubGroups
      }

      return chartData.value.map((item, index) => {
        // Get the last 7 days including current day
        const startIdx = Math.max(0, index - TRAILING_DAYS + 1)
        const windowData = chartData.value.slice(startIdx, index + 1)

        // Calculate trailing average for main value
        let totalStarted = 0
        let totalCancelled = 0

        windowData.forEach(d => {
          totalStarted += d.started
          totalCancelled += d.cancelled
        })

        const trailingValue = totalStarted > 0
          ? parseFloat(((totalCancelled / totalStarted) * 100).toFixed(1))
          : 0

        // Calculate trailing averages for both builds and durations
        const trailingBuilds = calculateTrailingForSubGroup(windowData, 'builds')
        const trailingDurations = calculateTrailingForSubGroup(windowData, 'durations')

        return {
          ...item,
          value: trailingValue,
          started: totalStarted,
          cancelled: totalCancelled,
          builds: trailingBuilds || item.builds,
          durations: trailingDurations || item.durations
        }
      })
    })

    const load = async () => {
      loading.value = true
      try {
        list.value = await service.getAllAdaptyWebhooks()
        filtered.value = list.value
      } finally {
        loading.value = false
      }

      await loadChartData()
    }

    const applyFilter = () => {
      const q = search.value.toLowerCase()
      const buildNum = buildNumberFilter.value.toLowerCase()

      let result = list.value

      if (q) {
        result = result.filter(item =>
          Object.values(item).some(val =>
            String(val).toLowerCase().includes(q)
          )
        )
      }

      if (buildNum) {
        result = result.filter(item => {
          const userAttrs = item.user_attributes || {}
          const itemBuildNumber = userAttrs.build_number || ''
          return String(itemBuildNumber).toLowerCase().includes(buildNum)
        })
      }

      filtered.value = result
    }

    const clearBuildFilter = () => {
      buildNumberFilter.value = ''
      applyFilter()
    }

    const formatDate = (val) => {
      if (!val) return '-'
      const date = val.seconds ? new Date(val.seconds * 1000) : new Date(val)
      return date.toLocaleString('tr-TR')
    }

    const truncate = (t, m) => !t ? '-' : t.length > m ? t.slice(0, m) + '...' : t

    const openExportModal = () => {
      selectedEventType.value = 'All';
      selectedFields.value = [...allFields.value];
      isExportModalVisible.value = true;
    };

    const closeExportModal = () => {
      isExportModalVisible.value = false;
    };

    const loadChartData = async (days = selectedDays.value, startDate = null, endDate = null) => {
      chartLoading.value = true
      try {
        const result = await service.getCancellationRateData(days, startDate, endDate, groupBy.value)

        if (result.chartData) {
          chartData.value = result.chartData
          buildNumbers.value = result.buildNumbers || []
          chartDetailData.value = result.chartData.map(item => ({
            date: item.date,
            label: `${item.value}% (${item.cancelled}/${item.started})`
          }))

          // Calculate average cancellation rate from total counts
          if (result.chartData.length > 0) {
            const started = result.chartData.reduce((acc, item) => acc + item.started, 0)
            const cancelled = result.chartData.reduce((acc, item) => acc + item.cancelled, 0)

            totalStarted.value = started
            totalCancelled.value = cancelled

            if (started > 0) {
              averageCancellationRate.value = ((cancelled / started) * 100).toFixed(1)
            } else {
              averageCancellationRate.value = 0
            }
          } else {
            averageCancellationRate.value = 0
            totalStarted.value = 0
            totalCancelled.value = 0
          }
        } else {
          chartData.value = []
          buildNumbers.value = []
          chartDetailData.value = []
          averageCancellationRate.value = 0
        }
      } catch (error) {
        console.error('Error loading chart data:', error)
        chartData.value = []
        buildNumbers.value = []
        chartDetailData.value = []
      } finally {
        chartLoading.value = false
      }
    }

    const selectDateRange = async (days) => {
      selectedDays.value = days
      isCustomRange.value = false
      showCustomDatePicker.value = false
      await loadChartData(days)
    }

    const toggleCustomDatePicker = () => {
      showCustomDatePicker.value = !showCustomDatePicker.value
      if (showCustomDatePicker.value) {
        isCustomRange.value = true
      }
    }

    const applyCustomDateRange = async () => {
      const start = new Date(customStartDate.value)
      const end = new Date(customEndDate.value)
      const diffTime = Math.abs(end - start)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      isCustomRange.value = true
      await loadChartData(diffDays + 1, customStartDate.value, customEndDate.value)
    }

    const changeGroupBy = async (type) => {
      groupBy.value = type
      // Keep the current date range when switching
      if (isCustomRange.value) {
        await loadChartData(selectedDays.value, customStartDate.value, customEndDate.value)
      } else {
        await loadChartData(selectedDays.value)
      }
    }

    const handleLegendClick = (visibleDatasets) => {
      if (!chartData.value || chartData.value.length === 0) return

      // If Total is visible, only calculate from Total
      if (visibleDatasets.includes('Total')) {
        const started = chartData.value.reduce((acc, item) => acc + item.started, 0)
        const cancelled = chartData.value.reduce((acc, item) => acc + item.cancelled, 0)

        totalStarted.value = started
        totalCancelled.value = cancelled

        if (started > 0) {
          averageCancellationRate.value = ((cancelled / started) * 100).toFixed(1)
        } else {
          averageCancellationRate.value = 0
        }
      } else {
        // Calculate average from visible build numbers only
        let started = 0
        let cancelled = 0

        visibleDatasets.forEach(datasetLabel => {
          // datasetLabel is now just the key itself (e.g., "143", "Weekly", "Unknown")
          chartData.value.forEach(item => {
            const build = item.builds?.[datasetLabel]
            if (build) {
              started += build.started
              cancelled += build.cancelled
            }
          })
        })

        totalStarted.value = started
        totalCancelled.value = cancelled

        if (started > 0) {
          averageCancellationRate.value = ((cancelled / started) * 100).toFixed(1)
        } else {
          averageCancellationRate.value = 0
        }
      }
    }

    const handleExport = () => {
      let dataToExport = list.value;
      if (selectedEventType.value !== 'All') {
        dataToExport = list.value.filter(item => item.event_type === selectedEventType.value);
      }

      if (dataToExport.length === 0) {
        alert("No data to export for the selected filter.");
        return;
      }

      if (selectedFields.value.length === 0) {
        alert("Please select at least one field to export.");
        return;
      }

      downloadCSV(dataToExport, selectedFields.value);
      closeExportModal();
    };

    const downloadCSV = (data, fields) => {
      if (!data || !data.length) {
        alert("No data to download.");
        return;
      }

      const headers = fields;

      const escapeCSV = (val) => {
        if (val === null || val === undefined) return '';
        let str = String(val);
        if (typeof val === 'object') str = JSON.stringify(val);
        str = str.replace(/\r?\n/g, ' ');
        if (str.includes(',') || str.includes('"')) {
          str = '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      const rows = data.map(s => {
        return fields.map(field => {
          let value
          if (field === 'vendor_product_id') {
            value = s.event_properties?.vendor_product_id
          } else if (field === 'environment') {
            value = s.event_properties?.environment
          } else if (field === 'build_number') {
            value = s.user_attributes?.build_number
          } else {
            value = s[field]
          }
          if (['receivedAt', 'event_datetime', 'profile_install_datetime'].includes(field)) {
            value = formatDate(value)
          }
          return escapeCSV(value)
        }).join(',')
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'adapty_webhooks.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const formatDuration = (ms) => {
      if (ms == null) return ''
      if (ms <= 0) return '0m'
      const minutes = Math.floor(ms / 60000)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)
      if (days > 0) {
        const remHours = hours % 24
        return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`
      }
      if (hours > 0) {
        const remMin = minutes % 60
        return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`
      }
      return `${minutes}m`
    }

    const exportLifecycles = async () => {
      if (exportingLifecycles.value) return
      exportingLifecycles.value = true
      try {
        const lifecycles = await lifecycleService.getCancellationLifecycles()
        if (lifecycles.length === 0) {
          alert('No cancellation lifecycles found.')
          return
        }
        const fmt = (ms) => ms ? new Date(ms).toISOString() : ''
        const headers = [
          'User ID', 'Start Date (UTC)', 'Cancel Date (UTC)', 'Duration', 'Duration (minutes)',
          'Plan Type', 'Vendor Product ID', 'Has Feedback', 'Main Reason',
          'Detailed Feedback', 'Additional Note', 'Feedback Text',
          'Repurchased', 'Repurchase Plan Type', 'Repurchase Vendor Product ID', 'Repurchase Date (UTC)'
        ]
        const escape = (val) => {
          if (val === null || val === undefined) return ''
          let str = String(val).replace(/\r?\n/g, ' ')
          if (str.includes(',') || str.includes('"')) str = '"' + str.replace(/"/g, '""') + '"'
          return str
        }
        const rows = lifecycles.map(r => [
          r.userId,
          fmt(r.startDateMs),
          fmt(r.cancelDateMs),
          formatDuration(r.durationMs),
          r.durationMs != null ? Math.round(r.durationMs / 60000) : '',
          r.planType || '',
          r.vendorProductId || '',
          r.feedback ? 'Yes' : 'No',
          r.feedback?.mainReason || '',
          r.feedback?.detailedFeedback || '',
          r.feedback?.additionalNote || '',
          r.feedback?.feedback || '',
          r.repurchased ? 'Yes' : 'No',
          r.repurchasePlanType || '',
          r.repurchasePackage || '',
          fmt(r.repurchaseDateMs)
        ])
        const csv = [headers.map(escape).join(','), ...rows.map(row => row.map(escape).join(','))].join('\n')
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', `subscription_lifecycles_${new Date().toISOString().slice(0, 10)}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (e) {
        console.error('Lifecycle export failed:', e)
        alert('Failed to export lifecycles: ' + (e.message || 'Unknown error'))
      } finally {
        exportingLifecycles.value = false
      }
    }

    onMounted(load)

    return {
      list,
      columns,
      distinctEventTypes,
      distinctUsers,
      eventVariant,
      filtered,
      search,
      buildNumberFilter,
      loading,
      applyFilter,
      clearBuildFilter,
      formatDate,
      truncate,
      downloadCSV,
      isExportModalVisible,
      selectedEventType,
      uniqueEventTypes,
      openExportModal,
      closeExportModal,
      handleExport,
      allFields,
      selectedFields,
      chartData,
      chartDetailData,
      chartLoading,
      buildNumbers,
      dateRanges,
      selectedDays,
      selectDateRange,
      isCustomRange,
      showCustomDatePicker,
      customStartDate,
      customEndDate,
      today,
      toggleCustomDatePicker,
      applyCustomDateRange,
      averageCancellationRate,
      totalStarted,
      totalCancelled,
      handleLegendClick,
      groupBy,
      changeGroupBy,
      useTrailingAverage,
      displayChartData,
      exportLifecycles,
      exportingLifecycles
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.nowrap { white-space: nowrap; }

/* Build number filter (secondary input with clear icon) */
.build-filter { position: relative; display: flex; align-items: center; }
.build-filter input {
  width: 200px;
  max-width: 100%;
  padding: 9px 32px 9px 12px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius);
  color: var(--text-primary);
  font-size: 13px;
}
.build-filter input::placeholder { color: var(--text-secondary); }
.build-filter input:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.2); }
.build-filter .clear-icon { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); cursor: pointer; color: var(--text-secondary); transition: color 0.2s; width: 14px; height: 14px; }
.build-filter .clear-icon:hover { color: var(--text-primary); }

.btn-primary { padding: 8px 14px; background-color: #6c63ff; color: white; border: none; border-radius: 8px; font-weight: 500; font-size: 13px; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 2px 8px rgba(108, 99, 255, 0.2); }
.btn-primary:hover:not(:disabled) { opacity: 0.9; box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.lifecycle-btn { background: linear-gradient(135deg, #22c55e, #16a34a); }

.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.7); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background-color: #1a1b2e; padding: 32px; border-radius: 16px; width: 500px; box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2); }
.modal-content h3 { margin-top: 0; font-size: 20px; }
.filter-group { margin-bottom: 24px; }
.filter-group p { color: #a9a9b3; margin-top: 0; margin-bottom: 12px; }
.event-type-select { width: 100%; padding: 12px; border-radius: 8px; background-color: #0f0f23; border: 1px solid #2c2d42; color: #fff; font-size: 16px; }
.fields-container { max-height: 200px; overflow-y: auto; display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; background-color: #0f0f23; padding: 12px; border-radius: 8px; border: 1px solid #2c2d42; }
.field-checkbox { display: flex; align-items: center; gap: 8px; }
.field-checkbox input { width: 16px; height: 16px; cursor: pointer; }
.field-checkbox label { cursor: pointer; user-select: none; font-size: 14px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 16px; }
.btn-secondary { padding: 12px 20px; background-color: transparent; color: #6c63ff; border: 1px solid #6c63ff; border-radius: 12px; font-weight: 500; cursor: pointer; }
.btn-secondary:hover { background-color: rgba(108, 99, 255, 0.1); }

.chart-header { margin-bottom: 16px; }
.chart-title-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; }
.chart-header h2 { font-size: 18px; margin: 0 0 6px 0; color: #fff; }
.chart-description { font-size: 13px; color: #a9a9b3; margin: 0; }

.chart-controls {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 12px;
  flex-wrap: wrap;
}

.group-by-selector {
  display: flex;
  gap: 6px;
  background: #0f0f23;
  padding: 3px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.group-btn {
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #a9a9b3;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.group-btn:hover {
  color: #fff;
}

.group-btn.active {
  background: #6c63ff;
  color: #fff;
}

.average-stat {
  margin-top: 10px;
  padding: 10px 14px;
  background: rgba(108, 99, 255, 0.1);
  border: 1px solid rgba(108, 99, 255, 0.3);
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.stat-label {
  font-size: 12px;
  color: #a9a9b3;
  font-weight: 500;
}

.stat-value {
  font-size: 16px;
  color: #6c63ff;
  font-weight: 600;
}

.stat-detail {
  font-size: 11px;
  color: #a9a9b3;
  font-weight: 400;
  margin-left: 4px;
}

.date-range-selector { display: flex; gap: 6px; flex-wrap: wrap; }
.range-btn {
  padding: 6px 12px;
  background: #0f0f23;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #a9a9b3;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.range-btn:hover {
  background: #1a1b2e;
  border-color: #6c63ff;
  color: #fff;
}
.range-btn.active {
  background: #6c63ff;
  border-color: #6c63ff;
  color: #fff;
  font-weight: 500;
}

.custom-date-picker {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  margin-top: 12px;
  padding: 14px;
  background: #0f0f23;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  flex-wrap: wrap;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-input-group label {
  font-size: 11px;
  color: #a9a9b3;
  font-weight: 500;
}

.date-input {
  padding: 6px 10px;
  background: #1a1b2e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  min-width: 130px;
}

.date-input:focus {
  outline: none;
  border-color: #6c63ff;
}

.apply-btn {
  padding: 6px 16px;
  background: #6c63ff;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  height: 32px;
}

.apply-btn:hover {
  background: #5a52d5;
}

.trailing-average-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #0f0f23;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #6c63ff;
}

.toggle-text {
  font-size: 12px;
  color: #a9a9b3;
  font-weight: 500;
  white-space: nowrap;
}

.toggle-label:hover .toggle-text {
  color: #fff;
}

.info-tooltip {
  position: relative;
  display: flex;
  align-items: center;
}

.info-icon {
  color: #a9a9b3;
  cursor: help;
  transition: color 0.2s;
}

.info-icon:hover {
  color: #6c63ff;
}

.tooltip-text {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  padding: 12px 14px;
  background: #2c2d42;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #e0e0e0;
  text-align: left;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  z-index: 100;
  transition: opacity 0.2s, visibility 0.2s;
  white-space: normal;
}

.tooltip-text::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: #2c2d42 transparent transparent transparent;
}

.info-tooltip:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

@media (max-width: 1024px) {
  .build-filter { width: 100%; }
  .build-filter input { width: 100%; }
}
</style>
